import React from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

export type FilterChipGroupProps<T extends string> =
  | {
      options: ChipOption<T>[];
      value: T;
      onChange: (next: T) => void;
      multi?: false;
      scrollable?: boolean;
      centered?: boolean;
      testID?: string;
    }
  | {
      options: ChipOption<T>[];
      value: T[];
      onChange: (next: T[]) => void;
      multi: true;
      scrollable?: boolean;
      centered?: boolean;
      testID?: string;
    };

/**
 * Self-contained filter chip group. Renders a horizontal row of pill-shaped
 * chips (optionally with a trailing count bubble) and supports both
 * single-select and multi-select modes.
 *
 * No `chip` slot exists on the theme today, so the active/inactive surface
 * colors are inlined `rgba(...)` literals tuned to the resident redesign.
 * Promote to `colors.chip.*` if/when that slot is added.
 */
export function FilterChipGroup<T extends string>(props: FilterChipGroupProps<T>) {
  const { options, scrollable = true, centered = false, testID } = props;
  const styles = useStyles();

  const isActive = (v: T) =>
    props.multi ? (props.value as T[]).includes(v) : props.value === v;

  const onPress = (v: T) => {
    if (props.multi) {
      const arr = props.value as T[];
      const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
      props.onChange(next);
    } else {
      (props.onChange as (n: T) => void)(v);
    }
  };

  const Container: any = scrollable ? ScrollView : View;
  const containerProps: any = scrollable
    ? {
        horizontal: true,
        showsHorizontalScrollIndicator: false,
        contentContainerStyle: [styles.row, centered && styles.centeredScroll],
        testID,
      }
    : { style: [styles.row, centered && styles.centeredView], testID };

  return (
    <Container {...containerProps}>
      {options.map((opt) => {
        const active = isActive(opt.value);
        return (
          <Pressable
            key={opt.value}
            onPress={() => onPress(opt.value)}
            style={[styles.chip, active && styles.chipActive]}
            testID={`${testID ?? 'chip'}-${opt.value}`}
          >
            <Text style={[styles.label, active && styles.labelActive]}>{opt.label}</Text>
            {opt.count != null ? (
              <Text style={[styles.count, active && styles.countActive]}>{opt.count}</Text>
            ) : null}
          </Pressable>
        );
      })}
    </Container>
  );
}

const useStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      row: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
        paddingVertical: 4,
      },
      centeredScroll: {
        flexGrow: 1,
        justifyContent: 'center',
      },
      centeredView: {
        justifyContent: 'center',
      },
      chip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 5,
        paddingHorizontal: 11,
        borderRadius: 999,
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
        gap: 6,
      },
      chipActive: {
        backgroundColor: 'rgba(70,114,254,0.16)',
        borderColor: 'rgba(70,114,254,0.45)',
      },
      label: {
        fontSize: 11,
        fontWeight: '600',
        color: colors.text.dim,
      },
      labelActive: {
        color: colors.text.primary,
      },
      count: {
        fontSize: 10,
        fontWeight: '700',
        color: colors.text.dim,
      },
      countActive: {
        color: '#5d80f5',
      },
    }),
  );
